import { Button_AI_Assist, Button_AI_Assist_Blue, Icon_Microphone } from "@assets/images";
import React, { useEffect, useState, useRef } from "react";
import { AIMessage, AssistantMessage, UserMessage } from "@interfaces/AIAssistTypes";
import classNames from "classnames";
import Textarea from "@mui/joy/Textarea";
import { IonButton } from "@ionic/react";
import { CPDActivityType, EvidenceCustomTag, IEvidence } from "@interfaces/Interfaces";
import { format } from "date-fns";
import * as StringUtils from "@utils/stringUtils";
import * as EvidenceUtils from "@utils/evidenceUtils";
import DataController from "@controllers/DataController";
import { evidencePreviewFields } from "@utils/aiAssistUtils";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { Capacitor } from "@capacitor/core";
import { useAIMessageScroll } from "@hooks/ai/useAIMessageScroll";

type Props = {
  message: AIMessage;
  handleUserMessage: (message: string) => void;
  handleRePrompt: () => void;
  handleGenerateEvidencePreview: () => void;
  handleSaveEvidencePreview: () => void;
  handleRefineEvidencePreview: () => void;
  handleUserRefineMessage: (data: IEvidence, message: string) => void;
  handleRetryEvidencePreview: () => void;
};

const getMessageRole = (role: "user" | "assistant") => {
  switch (role) {
    case "user":
      return "You";
    case "assistant":
      return "ParaFolio";
  }
};

const EvidencePreviewField: React.FC<{ field: keyof IEvidence; data: IEvidence; index: number }> = (props) => {
  const { field, data, index } = props;

  const fieldTitle = evidencePreviewFields.find((f) => f.key === field)?.label ?? field;
  const value = data[field];

  const formatValue = (field: keyof IEvidence, value: unknown): string | React.ReactNode => {
    if (value === undefined || value === null) return "";

    switch (field) {
      case "date":
      case "activityDate":
        return format(new Date(value as string), "d MMMM yyyy");
      case "numberOfHours":
        return StringUtils.getActivityDurationDisplayString(DataController.convertToHoursMinutes(value as string));
      case "customTags":
        return (
          <div className="flex flex-wrap gap-2">
            {(value as EvidenceCustomTag[]).map((tag) => (
              <div
                className="bg-ai-assist-preview-background rounded py-0.5 px-1.5 text-black font-medium text-acc-16px"
                key={tag.tag}
              >
                {tag.tag}
              </div>
            ))}
          </div>
        );
      case "reflections":
      case "benefits":
      case "objectives":
      case "title":
        return EvidenceUtils.stripHtmlTags(value as string);
      case "activity":
        return (value as CPDActivityType | undefined)?.type ?? "";
      default:
        return value as string;
    }
  };

  const formattedValue = formatValue(field, value);

  const valueIsEmpty = formattedValue === "";

  return (
    <div className={classNames("relative w-full flex flex-col", { "-mt-4": index === 0 })}>
      <div className="rounded p-3 self-stretch bg-ai-assist-preview-background">
        <div className="text-black text-acc-14px font-semibold mb-2">{fieldTitle}</div>
        {valueIsEmpty ? <div className="h-6" /> : <div className="text-black text-acc-16px">{formattedValue}</div>}
      </div>
    </div>
  );
};

const animatedMessageIds = new Set<string>();

export const clearAnimatedMessageIds = () => {
  animatedMessageIds.clear();
};

const AIAssistMessage: React.FC<Props> = (props) => {
  const [messageInput, setMessageInput] = useState<string>("");
  const [isListening, setIsListening] = useState<boolean>(false);
  const isWeb = Capacitor.getPlatform() === "web";
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Check if this message has already been animated
  const hasAnimated = animatedMessageIds.has(props.message.id);

  useEffect(() => {
    if (!hasAnimated) {
      animatedMessageIds.add(props.message.id);
    }
  }, [props.message.id, hasAnimated]);

  useAIMessageScroll(props.message, hasAnimated);

  const { transcript, resetTranscript, browserSupportsSpeechRecognition, isMicrophoneAvailable } =
    useSpeechRecognition();

  useEffect(() => {
    if (isWeb && isListening && transcript) {
      setMessageInput(transcript);
    }
  }, [transcript, isListening, isWeb]);

  useEffect(() => {
    return () => {
      if (isWeb && isListening) {
        SpeechRecognition.stopListening();
      }
    };
  }, [isWeb, isListening]);

  useEffect(() => {
    const isAssistantMessage = "type" in props.message;
    if (!isAssistantMessage) {
      const message = props.message as UserMessage;
      if ((message.status === "message-input" || message.status === "evidence-preview-refine") && textareaRef.current) {
        setTimeout(() => {
          textareaRef.current?.focus();
        }, 0);
      }
    }
  }, [props.message]);

  const handleMicrophoneClick = async () => {
    if (!isWeb || !browserSupportsSpeechRecognition) {
      return;
    }

    if (isListening) {
      setIsListening(false);
      await SpeechRecognition.stopListening();
    } else {
      resetTranscript();
      setIsListening(true);
      try {
        if (!isMicrophoneAvailable) {
          console.error("Microphone not available");
          setIsListening(false);
          return;
        }
        await SpeechRecognition.startListening({
          language: "en-GB",
          continuous: true,
        });
      } catch (error) {
        console.error("Error starting speech recognition:", error);
        setIsListening(false);
      }
    }
  };

  const handleSendMessage = async () => {
    if (messageInput.trim().length === 0) return;

    if (isWeb && isListening) {
      await SpeechRecognition.stopListening();
      setIsListening(false);
    }

    const message = props.message as UserMessage;
    if (message.status === "evidence-preview-refine") {
      props.handleUserRefineMessage(message.data as IEvidence, messageInput);
    } else {
      props.handleUserMessage(messageInput);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const isAssistantMessage = "type" in props.message;

  if (isAssistantMessage) {
    const message = props.message as AssistantMessage;
    const isPrompt = message.type === "prompt" || message.type === "re-prompt";
    const isLoading = message.type === "evidence-preview-loading";

    const isEvidencePreview = message.type === "evidence-preview";
    const data = message.data;

    if (isEvidencePreview && data) {
      return (
        <>
          <div
            id={`${message.id}-evidence-preview-anchor`}
            className="invisible"
          />
          {evidencePreviewFields.map((item, index) => (
            <div
              key={item.key}
              className={classNames({ "ai-message-fade-in": !hasAnimated })}
            >
              <EvidencePreviewField
                field={item.key as keyof IEvidence}
                data={data}
                index={index}
              />
            </div>
          ))}
        </>
      );
    }

    if (isLoading) {
      return (
        <>
          <style>{`
            @keyframes ai-bounce-ellipsis {
              0%, 80%, 100% {
                transform: translateY(0);
                opacity: 0.7;
              }
              40% {
                transform: translateY(-8px);
                opacity: 1;
              }
            }
          `}</style>
          <div
            id={message.id}
            className={classNames("relative w-fit flex flex-col max-w-lg flex-shrink-0", {
              "ai-message-fade-in": !hasAnimated,
            })}
          >
            <div className="rounded p-3 self-start bg-ai-assist-preview-background">
              <div className="text-black text-acc-16px font-extrabold mb-4">{getMessageRole(props.message.role)}</div>
              <div className="text-black text-acc-16px flex items-center gap-1.5 mt-2">
                <span
                  className="inline-block w-1.5 h-1.5 bg-black rounded-full"
                  style={{
                    animation: "ai-bounce-ellipsis 1.4s infinite ease-in-out",
                    animationDelay: "0s",
                  }}
                ></span>
                <span
                  className="inline-block w-1.5 h-1.5 bg-black rounded-full"
                  style={{
                    animation: "ai-bounce-ellipsis 1.4s infinite ease-in-out",
                    animationDelay: "0.2s",
                  }}
                ></span>
                <span
                  className="inline-block w-1.5 h-1.5 bg-black rounded-full"
                  style={{
                    animation: "ai-bounce-ellipsis 1.4s infinite ease-in-out",
                    animationDelay: "0.4s",
                  }}
                ></span>
              </div>
            </div>
          </div>
          <div
            id={`${message.id}-scroll-anchor`}
            className="invisible"
          />
        </>
      );
    }

    return (
      <div
        id={message.id}
        className={classNames("relative w-fit flex flex-col max-w-lg flex-shrink-0", {
          "ai-message-fade-in": !hasAnimated,
        })}
      >
        {isPrompt && (
          <div className="absolute -top-4 -left-4 size-8 bg-ai-assist-prompt-icon rounded-full flex items-center justify-center">
            <img
              src={Button_AI_Assist}
              alt="Mic"
              className="w-3 h-5"
            />
          </div>
        )}
        <div className="rounded p-3 self-start bg-ai-assist-assistant-message-background">
          <div className="text-white text-acc-16px font-extrabold mb-2">{getMessageRole(props.message.role)}</div>
          <div
            className="text-white text-acc-16px"
            dangerouslySetInnerHTML={{ __html: props.message.content ?? "" }}
          />
        </div>
        {message.type === "re-prompt" && (
          <div className="flex justify-end gap-2 w-fit self-end">
            <IonButton
              mode="ios"
              className="flex h-8 mt-2 self-center small-button ai-flow-outline-button"
              onClick={() => props.handleRePrompt()}
            >
              <img
                src={Button_AI_Assist}
                alt="Mic"
                className="w-3 h-5"
              />
              <div className="font-semibold tracking-default acc-underline text-white ml-2.5">{"Try again"}</div>
            </IonButton>
            <IonButton
              mode="ios"
              className="flex w-20 h-8 mt-2 self-center small-button"
              onClick={() => props.handleGenerateEvidencePreview()}
            >
              <div className="font-semibold tracking-default acc-underline text-cta-blue">{"Yes"}</div>
            </IonButton>
          </div>
        )}
        {message.type === "evidence-preview-save" && (
          <div className="flex gap-2 w-fit self-end">
            <IonButton
              mode="ios"
              fill="outline"
              className="flex w-20 h-8 mt-2 self-center small-button ai-flow-outline-button white-border-button"
              onClick={() => props.handleSaveEvidencePreview()}
            >
              <div className="font-semibold tracking-default acc-underline text-white">{"Save"}</div>
            </IonButton>
            <IonButton
              mode="ios"
              className="flex h-8 mt-2 self-center small-button"
              onClick={() => props.handleRefineEvidencePreview()}
            >
              <img
                src={Button_AI_Assist_Blue}
                alt="Mic"
                className="w-3 h-5"
              />
              <div className="font-semibold tracking-default acc-underline text-cta-blue ml-2.5">{"Refine"}</div>
            </IonButton>
          </div>
        )}
        {message.type === "evidence-preview-error" && (
          <div className="flex justify-end gap-2 w-fit self-end">
            <IonButton
              mode="ios"
              className="flex h-8 mt-2 self-center small-button"
              onClick={() => props.handleRetryEvidencePreview()}
            >
              <div className="font-semibold tracking-default acc-underline text-cta-blue">{"Retry"}</div>
            </IonButton>
          </div>
        )}
        <div
          id={`${message.id}-scroll-anchor`}
          className="invisible"
        />
      </div>
    );
  }

  const message = props.message as UserMessage;

  return (
    <div
      id={message.id}
      className={classNames("relative w-full flex flex-col", {
        "ai-message-fade-in": !hasAnimated,
      })}
    >
      <div className={classNames("rounded p-3 max-w-md w-full self-end bg-ai-assist-user-message-background", {
        "bg-ai-assist-user-message-background/70": message.status === "message-input" || message.status === "evidence-preview-refine",

      })}>
        <div className="text-black text-acc-16px font-extrabold mb-2">{getMessageRole(message.role)}</div>
        {(message.status === "message-input" || message.status === "evidence-preview-refine") && (
          <Textarea
            placeholder="Describe your CPD..."
            value={messageInput}
            onChange={(event) => setMessageInput(event.target.value)}
            onKeyDown={handleKeyDown}
            slotProps={{
              textarea: {
                ref: textareaRef,
              },
            }}
            variant="plain"
            sx={{
              "--Textarea-placeholderOpacity": 0.6,
              "--Textarea-focusedThickness": "0px",
              backgroundColor: "transparent",
              minHeight: "28px",
              color: messageInput.length ? "#000000" : "#000000",
              padding: 0,
              fontSize: "16px",
              fontWeight: "normal",
              overflow: "hidden",
              caretColor: "#000000",
              ":hover": {
                color: messageInput.length ? "#000000" : "#000000",
              },
              "--Textarea-focusedInset": "var(--any, )",
              "--Textarea-focusedHighlight": "rgba(255, 255, 255, 0.8)",
            }}
          />
        )}
        {((message.status !== "message-input" && message.content) ||
          (message.status === "evidence-preview-refine" && message.content)) && (
            <div className="text-black text-acc-16px">{message.content}</div>
          )}
      </div>
      {message.status === "message-input" && (
        <div className="flex justify-end gap-2">
          {isWeb && browserSupportsSpeechRecognition && (
            <IonButton
              mode="ios"
              fill="outline"
              className="relative flex h-8 mt-2 self-center small-button ai-flow-outline-button white-border-button"
              onClick={handleMicrophoneClick}
            >
              {isListening && <div className="absolute h-44 w-44 bg-white/40" />}
              <img
                src={Icon_Microphone}
                alt="Mic"
                className="w-3 h-5"
              />
            </IonButton>
          )}
          <IonButton
            mode="ios"
            className="flex w-28 h-8 mt-2 self-center small-button"
            disabled={messageInput.trim().length === 0}
            onClick={async () => {
              if (isWeb && isListening) {
                await SpeechRecognition.stopListening();
                setIsListening(false);
              }
              props.handleUserMessage(messageInput);
            }}
          >
            <div className="font-semibold tracking-default acc-underline text-cta-blue">{"Done"}</div>
          </IonButton>
        </div>
      )}
      {message.status === "evidence-preview-refine" && (
        <div className="flex justify-end gap-2">
          {isWeb && browserSupportsSpeechRecognition && (
            <IonButton
              mode="ios"
              fill="outline"
              className="flex h-8 mt-2 self-center small-button ai-flow-outline-button white-border-button"
              onClick={handleMicrophoneClick}
            >
              {isListening && <div className="absolute h-44 w-44 bg-white/40" />}
              <img
                src={Icon_Microphone}
                alt="Mic"
                className="w-3 h-5"
              />
            </IonButton>
          )}
          <IonButton
            mode="ios"
            className="flex w-28 h-8 mt-2 self-center small-button"
            disabled={messageInput.trim().length === 0}
            onClick={async () => {
              if (isWeb && isListening) {
                await SpeechRecognition.stopListening();
                setIsListening(false);
              }
              props.handleUserRefineMessage(message.data as IEvidence, messageInput);
            }}
          >
            <div className="font-semibold tracking-default acc-underline text-cta-blue">{"Done"}</div>
          </IonButton>
        </div>
      )}
      <div
        id={`${message.id}-scroll-anchor`}
        className="invisible"
      />
    </div>
  );
};

export default AIAssistMessage;
